import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { trendsService, TrendListItem } from '@/services/trends-service'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Activity, 
  TrendingUp, 
  Heart, 
  Eye,
  Filter,
  BarChart3,
  Map,

  BookOpen,
  AlertTriangle,
  Calendar,
  Hash,
  Zap,
  ChevronDown,
  ChevronUp
} from 'lucide-react'

// Utility functions for data processing
const formatTime = (unixSeconds: number): string => {
  const now = Date.now() / 1000
  const diff = now - unixSeconds
  
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return `${Math.floor(diff / 604800)}w ago`
}



const formatNumber = (num: number): string => {
  if (num >= 1000000000000) return `${(num / 1000000000000).toFixed(1)}T`
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

const formatPercentage = (rate: number): string => {
  return `${(rate * 100).toFixed(1)}%`
}

// Computed metrics
const computeMetrics = (trend: TrendListItem) => {
  const momentum = (
    0.35 * trend.mean_velocity_like +
    0.2 * trend.mean_velocity_comment +
    0.25 * trend.median_engagement_rate * 100 +
    0.1 * trend.mean_residual_like +
    0.1 * Math.log10(trend.reach + 1)
  )
  
  const heat = (
    0.6 * trend.mean_velocity_like +
    0.4 * trend.mean_velocity_comment
  )
  
  const endurance = (
    0.7 * trend.mean_residual_like +
    0.3 * (trend.median_age / 86400) // Convert to days
  )
  
  const efficiency = momentum / Math.max(trend.post_count, 1)
  
  return { momentum, heat, endurance, efficiency }
}

const getLifecycleStage = (medianAge: number): { stage: string; color: string } => {
  const hours = medianAge / 3600
  if (hours <= 24) return { stage: 'Fresh', color: 'bg-green-100 text-green-800' }
  if (hours <= 168) return { stage: 'Emerging', color: 'bg-blue-100 text-blue-800' }
  if (hours <= 720) return { stage: 'Maturing', color: 'bg-yellow-100 text-yellow-800' }
  return { stage: 'Enduring', color: 'bg-purple-100 text-purple-800' }
}



// Trend Card Component
const TrendCard: React.FC<{ 
  trend: TrendListItem; 
  isSelected: boolean; 
  onSelect: () => void;
}> = ({ trend, isSelected, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const metrics = useMemo(() => computeMetrics(trend), [trend])
  const lifecycle = useMemo(() => getLifecycleStage(trend.median_age), [trend.median_age])
  const [primaryWords, secondaryWords] = useMemo(() => [
    trend.word_representation.slice(0, 2),
    trend.word_representation.slice(2, 7)
  ], [trend.word_representation])
  
  const hasContentAnalysis = useMemo(() => !!(
    trend.description_text || 
    trend.hook_patterns?.length || 
    trend.angle_map?.length || 
    trend.rhetorical_devices?.length ||
    trend.cultural_trend_cues ||
    trend.risk_policy_flags?.length
  ), [trend])

  const riskFlags = trend.risk_policy_flags || []
  const riskLevel = useMemo(() => riskFlags.length > 0 
    ? riskFlags.reduce((max, flag) => {
        if (flag.severity === 'high') return 'high'
        if (flag.severity === 'medium' && max !== 'high') return 'medium'
        return max || 'low'
      }, '')
    : null, [riskFlags])

  const getRiskColor = (level: string) => ({
    high: 'bg-red-500',
    medium: 'bg-orange-500', 
    low: 'bg-yellow-500'
  }[level] || 'bg-gray-400')

    
  
  return (
    <Card className={`relative transition-all hover:shadow-lg ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30 dark:bg-blue-950/30' : 'hover:shadow-xl'} overflow-hidden`}>
      {/* Selection checkbox */}
      {isSelected && (
        <div className="absolute top-3 left-3 w-5 h-5 bg-blue-500 rounded border-2 border-background shadow-sm flex items-center justify-center z-10">
          <div className="w-2 h-2 bg-background rounded-sm"></div>
        </div>
      )}
      
      <div className="cursor-pointer" onClick={onSelect}>
        {/* Header - Identity Section */}
        <CardHeader className="pb-6 px-8 pt-8">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <CardTitle className="text-xl font-bold leading-tight text-foreground mb-2">
                {primaryWords.join(' ').toUpperCase()}
              </CardTitle>
              <div className="flex flex-wrap gap-1.5">
                {secondaryWords.map((word, i) => (
                  <Badge key={i} variant="outline" className="text-xs px-2 py-0.5">{word}</Badge>
                ))}
              </div>
            </div>
            
            {/* Top-right indicators */}
            <div className="flex items-start gap-2 flex-col">
              <Badge className={`${lifecycle.color} text-xs px-2 py-1`}>{lifecycle.stage}</Badge>
              {riskLevel && (
                <div className="flex items-center gap-1.5 bg-background rounded-full px-2 py-1 border shadow-sm" title={`${riskFlags.length} risk flag(s) - highest: ${riskLevel}`}>
                  <div className={`w-2.5 h-2.5 rounded-full ${getRiskColor(riskLevel)}`}></div>
                  <span className="text-xs font-medium text-foreground">{riskFlags.length}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description snippet */}
          {trend.description_text && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mt-2">
              {trend.description_text}
            </p>
          )}
        </CardHeader>
        
        <CardContent className="space-y-4 px-8">
          {/* Momentum display */}
          <div className="flex justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {metrics.momentum.toFixed(1)}
              </div>
              <div className="text-sm text-muted-foreground">Momentum</div>
            </div>
          </div>
          
          {/* Key metrics - 2x2 grid */}
          <div className="grid grid-cols-2 gap-3 text-sm text-foreground">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-500" />
              <span>Heat: {metrics.heat.toFixed(1)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              <span>Endurance: {metrics.endurance.toFixed(1)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              <span>{formatPercentage(trend.median_engagement_rate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-green-500" />
              <span>{formatNumber(trend.reach)}</span>
            </div>
          </div>
          
          {/* Micro velocity bars */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Like Velocity</span>
              <span>{trend.mean_velocity_like.toFixed(1)}/min</span>
            </div>
            <div className="w-full bg-muted/30 rounded-full h-1.5">
              <div 
                className="bg-pink-500 h-1.5 rounded-full" 
                style={{ width: `${Math.min(trend.mean_velocity_like / 10 * 100, 100)}%` }}
              />
            </div>
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Comment Velocity</span>
              <span>{trend.mean_velocity_comment.toFixed(1)}/min</span>
            </div>
            <div className="w-full bg-muted/30 rounded-full h-1.5">
              <div 
                className="bg-blue-500 h-1.5 rounded-full" 
                style={{ width: `${Math.min(trend.mean_velocity_comment / 5 * 100, 100)}%` }}
              />
            </div>
          </div>
        </CardContent>
      </div>

      {/* Actions Bar */}
      <div className="border-t bg-background px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {hasContentAnalysis ? (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 font-medium">
              <BookOpen className="w-3 h-3" />
              Content insights available
            </span>
          ) : (
            <span className="text-xs text-muted-foreground font-medium">No content insights yet</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {hasContentAnalysis && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted/50"
            >
              <span>{isExpanded ? 'Hide' : 'Show'} details</span>
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>

      {/* Expandable content drawer */}
      {isExpanded && hasContentAnalysis && (
        <div className="border-t bg-background">
          <div className="px-8 py-6 space-y-8 max-h-96 overflow-y-auto">
            {/* Content Insights Section */}
            {(trend.hook_patterns?.length || trend.angle_map?.length || trend.rhetorical_devices?.length) && (
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                  Content Insights
                </h4>
                
                {/* Hook Patterns */}
                {trend.hook_patterns && trend.hook_patterns.length > 0 && (
                  <div className="bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-3">
                    <div className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                      Hook Patterns ({trend.hook_patterns.length})
                    </div>
                    <div className="space-y-2">
                      {trend.hook_patterns.map((hook, i) => (
                        <div key={i} className="bg-background rounded p-2 text-xs border">
                          <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">{hook.pattern}</div>
                          <div className="text-muted-foreground italic">"{hook.example}"</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Angles */}
                {trend.angle_map && trend.angle_map.length > 0 && (
                  <div className="bg-purple-50/50 dark:bg-purple-950/20 rounded-lg p-3">
                    <div className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
                      Content Angles ({trend.angle_map.length})
                    </div>
                    <div className="space-y-2">
                      {trend.angle_map.map((angle, i) => (
                        <div key={i} className="bg-background rounded p-2 text-xs border">
                          <div className="font-medium text-purple-700 dark:text-purple-300 mb-1">{angle.angle}</div>
                          <div className="text-muted-foreground">{angle.representative}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rhetorical Devices */}
                {trend.rhetorical_devices && trend.rhetorical_devices.length > 0 && (
                  <div className="bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3">
                    <div className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                      Rhetorical Devices ({trend.rhetorical_devices.length})
                    </div>
                    <div className="space-y-2">
                      {trend.rhetorical_devices.map((device, i) => (
                        <div key={i} className="bg-background rounded p-2 text-xs border">
                          <div className="font-medium text-green-700 dark:text-green-300 mb-1">{device.device}</div>
                          <div className="space-y-1">
                            {device.examples.slice(0, 2).map((example, j) => (
                              <div key={j} className="text-muted-foreground italic">"{example}"</div>
                            ))}
                            {device.examples.length > 2 && (
                              <div className="text-muted-foreground/70">+{device.examples.length - 2} more examples</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cultural Signals */}
            {trend.cultural_trend_cues && (
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  Cultural Signals
                </h4>
                <div className="bg-orange-50/50 dark:bg-orange-950/20 rounded-lg p-3 space-y-3">
                  {trend.cultural_trend_cues.named_entities && trend.cultural_trend_cues.named_entities.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">Entities</div>
                      <div className="flex flex-wrap gap-1">
                        {trend.cultural_trend_cues.named_entities.map((entity, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{entity}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {trend.cultural_trend_cues.time_urgency_markers && trend.cultural_trend_cues.time_urgency_markers.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">Time Urgency Markers</div>
                      <div className="flex flex-wrap gap-1">
                        {trend.cultural_trend_cues.time_urgency_markers.map((marker, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{marker}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Risk & Policy */}
            {riskFlags.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Risk & Policy Flags
                </h4>
                <div className="space-y-2">
                  {riskFlags.map((flag, i) => (
                    <div key={i} className="bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="destructive" className="text-xs">{flag.severity}</Badge>
                        <span className="font-medium text-red-800 dark:text-red-200">{flag.flag}</span>
                      </div>
                      <div className="text-sm text-red-700 dark:text-red-300 mb-2">"{flag.evidence}"</div>
                      <div className="text-sm text-green-700 dark:text-green-300 bg-green-50/50 dark:bg-green-950/20 rounded p-2">
                        <span className="font-medium">Suggested rewrite:</span> "{flag.safe_rewrite}"
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Style & Cadence */}
            {trend.style_cadence_norms && (
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  Style & Cadence
                </h4>
                <div className="bg-muted/50 rounded-lg p-3 text-sm text-foreground">
                  {trend.style_cadence_norms}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

// Filter segments
const filterSegments = [
  { id: 'all', label: 'All Trends', filter: () => true },
  { id: 'fresh', label: 'Fresh', filter: (t: TrendListItem) => t.median_age <= 86400 },
  { id: 'rising', label: 'Rising', filter: (t: TrendListItem) => computeMetrics(t).heat > 5 },
  { id: 'enduring', label: 'Enduring', filter: (t: TrendListItem) => computeMetrics(t).endurance > 3 },
  { id: 'broad', label: 'Broad', filter: (t: TrendListItem) => t.reach > 100000 },
  { id: 'niche', label: 'Niche', filter: (t: TrendListItem) => t.reach < 10000 && computeMetrics(t).heat > 3 },
  { id: 'quality', label: 'High Quality', filter: (t: TrendListItem) => t.median_engagement_rate > 0.05 },
  { id: 'risk_flagged', label: 'Risk Flagged', filter: (t: TrendListItem) => !!(t.risk_policy_flags && t.risk_policy_flags.length > 0) },
]

// Centralized quadrant definitions with consistent colors
const QUADRANTS = {
  'Viral & Enduring': { hex: '#8b5cf6', bg: 'bg-purple-50 dark:bg-purple-950/20', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
  'Flash Viral': { hex: '#ef4444', bg: 'bg-red-50 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  'Slow Burn': { hex: '#3b82f6', bg: 'bg-blue-50 dark:bg-blue-950/20', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
  'Emerging': { hex: '#6b7280', bg: 'bg-gray-50 dark:bg-gray-950/20', text: 'text-gray-700 dark:text-gray-300', dot: 'bg-gray-500' }
} as const

// Enhanced Trend Map Component with better visualization and interactivity
const TrendMap: React.FC<{ trends: TrendListItem[]; onTrendClick?: (trend: TrendListItem) => void }> = ({ 
  trends, 
  onTrendClick 
}) => {
  const [hoveredTrend, setHoveredTrend] = useState<TrendListItem | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  
  // Memoize expensive calculations
  const { maxHeat, maxEndurance, trendMetrics } = useMemo(() => {
    const metrics = trends.map(t => ({ trend: t, metrics: computeMetrics(t) }))
    return {
      maxHeat: Math.max(...metrics.map(m => m.metrics.heat), 1),
      maxEndurance: Math.max(...metrics.map(m => m.metrics.endurance), 1),
      trendMetrics: metrics
    }
  }, [trends])
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY })
  }, [])

  const getQuadrant = (heat: number, endurance: number) => {
    const heatNorm = heat / maxHeat
    const enduranceNorm = endurance / maxEndurance
    
    if (heatNorm > 0.5 && enduranceNorm > 0.5) return 'Viral & Enduring'
    if (heatNorm > 0.5 && enduranceNorm <= 0.5) return 'Flash Viral'  
    if (heatNorm <= 0.5 && enduranceNorm > 0.5) return 'Slow Burn'
    return 'Emerging'
  }
  
  return (
    <div className="space-y-6">
      {/* Enhanced explanation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-500" />
              Heat (X-Axis)
            </h4>
            <p className="text-sm text-gray-600">
              Measures how fast a trend is gaining likes and comments right now. 
              High heat = viral momentum, low heat = steady or declining activity.
            </p>
            <div className="mt-2 text-xs text-gray-500">
              Formula: 60% like velocity + 40% comment velocity
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              Endurance (Y-Axis)
            </h4>
            <p className="text-sm text-gray-600">
              Shows how long engagement persists after initial posts. 
              High endurance = lasting interest, low endurance = quick fading.
            </p>
            <div className="mt-2 text-xs text-gray-500">
              Formula: 70% residual engagement + 30% trend age
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Quadrant legend */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(QUADRANTS).map(([name, colors]) => (
          <div key={name} className={`flex items-center gap-2 p-2 ${colors.bg} rounded-lg`}>
            <div className={`w-3 h-3 ${colors.dot} rounded-full`}></div>
            <span className={`text-sm font-medium ${colors.text}`}>{name}</span>
          </div>
        ))}
      </div>
      
             {/* Enhanced scatter plot with external axis labels */}
       <div className="relative flex">
         {/* Y-axis label (left side) */}
         <div className="flex items-center justify-center w-16">
           <div className="transform -rotate-90 whitespace-nowrap">
             <div className="flex items-center gap-2 bg-background px-3 py-1 rounded-full shadow-sm border">
               <Activity className="w-4 h-4 text-blue-500" />
               <span className="text-sm font-medium text-foreground">Endurance (Staying Power)</span>
             </div>
           </div>
         </div>
         
         {/* Main chart area with bottom label */}
         <div className="flex-1 my-12">
           <div className="relative w-full h-[500px] bg-gradient-to-br from-muted/20 to-muted/40 rounded-lg border-2 border-border">
             <svg 
               width="100%" 
               height="100%" 
               className="absolute inset-0"
               onMouseMove={handleMouseMove}
               onMouseLeave={() => setHoveredTrend(null)}
             >
               {/* Grid lines */}
               <defs>
                 <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                   <path d="M 50 0 L 0 0 0 50" fill="none" stroke="hsl(var(--border))" strokeWidth="1"/>
                 </pattern>
               </defs>
               <rect width="100%" height="100%" fill="url(#grid)" opacity="0.5" />
               
               {/* Quadrant separators */}
               <line x1="50%" y1="0%" x2="50%" y2="100%" stroke="hsl(var(--border))" strokeWidth="2" strokeDasharray="5,5" />
               <line x1="0%" y1="50%" x2="100%" y2="50%" stroke="hsl(var(--border))" strokeWidth="2" strokeDasharray="5,5" />
               
               {/* Quadrant labels aligned to computed quadrants (SVG y-axis flips visually) */}
               <text x="25%" y="25%" textAnchor="middle" className="text-sm font-semibold" opacity="0.7" fill={QUADRANTS['Slow Burn'].hex}>
                 Slow Burn
               </text>
               <text x="75%" y="25%" textAnchor="middle" className="text-sm font-semibold" opacity="0.7" fill={QUADRANTS['Viral & Enduring'].hex}>
                 Viral & Enduring
               </text>
               <text x="25%" y="75%" textAnchor="middle" className="text-sm font-semibold" opacity="0.7" fill={QUADRANTS['Emerging'].hex}>
                 Emerging
               </text>
               <text x="75%" y="75%" textAnchor="middle" className="text-sm font-semibold" opacity="0.7" fill={QUADRANTS['Flash Viral'].hex}>
                 Flash Viral
               </text>
               
               {/* Trend bubbles */}
               {trendMetrics.map(({ trend, metrics }) => {
                 const x = (metrics.heat / maxHeat) * 80 + 10 // 10% margin
                 const y = 90 - (metrics.endurance / maxEndurance) * 80 // Flip Y axis, 10% margin
                 const baseSize = Math.log10(trend.reach + 1) * 1.5 + 3
                 const size = hoveredTrend === trend ? baseSize * 1.3 : baseSize
                 const quadrantName = getQuadrant(metrics.heat, metrics.endurance)
                 const color = QUADRANTS[quadrantName].hex
                 
                 return (
                   <g key={trend.trend_id}>
                     {/* Outer ring for engagement rate */}
                     <circle
                       cx={`${x}%`}
                       cy={`${y}%`}
                       r={size + 2}
                       fill="none"
                       stroke={color}
                       strokeWidth={trend.median_engagement_rate * 50 + 0.5}
                       opacity="0.3"
                     />
                     {/* Main bubble */}
                     <circle
                       cx={`${x}%`}
                       cy={`${y}%`}
                       r={size}
                       fill={color}
                       opacity={hoveredTrend === trend ? 0.9 : 0.7}
                       stroke={color}
                       strokeWidth="2"
                       className="cursor-pointer transition-all duration-200"
                       onMouseEnter={() => setHoveredTrend(trend)}
                       onClick={() => onTrendClick?.(trend)}
                     />
                     {/* Trend label for larger bubbles */}
                     {size > 8 && (
                       <text
                         x={`${x}%`}
                         y={`${y + 2}%`}
                         textAnchor="middle"
                         className="fill-white text-xs font-medium pointer-events-none"
                       >
                         {trend.word_representation[0]?.slice(0, 8)}
                       </text>
                     )}
                   </g>
                 )
               })}
             </svg>
             
             {/* Hover tooltip */}
             {hoveredTrend && (
               <div 
                 className="absolute pointer-events-none z-10 bg-black text-white p-3 rounded-lg shadow-lg max-w-xs"
                 style={{ 
                   left: mousePos.x - 150, 
                   top: mousePos.y - 100,
                   transform: 'translate(-50%, -100%)'
                 }}
               >
                 <div className="font-semibold text-sm mb-1">
                   {hoveredTrend.word_representation.slice(0, 3).join(' ')}
                 </div>
                 <div className="space-y-1 text-xs">
                   <div>Heat: {computeMetrics(hoveredTrend).heat.toFixed(1)}</div>
                   <div>Endurance: {computeMetrics(hoveredTrend).endurance.toFixed(1)}</div>
                   <div>Reach: {formatNumber(hoveredTrend.reach)}</div>
                   <div>Engagement: {formatPercentage(hoveredTrend.median_engagement_rate)}</div>
                   <div className="pt-1 border-t border-gray-600">
                     <span className="text-white font-medium">
                       {getQuadrant(
                         computeMetrics(hoveredTrend).heat, 
                         computeMetrics(hoveredTrend).endurance
                       )}
                     </span>
                   </div>
                 </div>
               </div>
             )}
           </div>
           
           {/* X-axis label (centered below chart) */}
           <div className="flex justify-center mt-4">
             <div className="flex items-center gap-2 bg-background px-3 py-1 rounded-full shadow-sm border">
               <TrendingUp className="w-4 h-4 text-red-500" />
               <span className="text-sm font-medium text-foreground">Heat (Viral Speed)</span>
             </div>
           </div>
         </div>
       </div>
      
      {/* Legend for bubble encoding */}
      <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
            <div className="w-4 h-4 bg-purple-500 rounded-full ml-1"></div>
            <div className="w-6 h-6 bg-purple-500 rounded-full ml-1"></div>
          </div>
          <span>Bubble Size = Reach (log scale)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-500 rounded-full bg-transparent"></div>
          <span>Ring Thickness = Engagement Rate</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex">
            {Object.values(QUADRANTS).map((colors, i) => (
              <div key={i} className="w-3 h-3 rounded-full -ml-1 first:ml-0 border border-background" style={{ backgroundColor: colors.hex }}></div>
            ))}
          </div>
          <span>Color = Trend Category</span>
        </div>
      </div>
    </div>
  )
}

// Main Social Trends Page
const SocialTrendsPage: React.FC = () => {
  const [trends, setTrends] = useState<TrendListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedSegment, setSelectedSegment] = useState('all')
  const [selectedTrends, setSelectedTrends] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<'momentum' | 'heat' | 'endurance' | 'engagement' | 'reach'>('momentum')
  const [searchTokens, setSearchTokens] = useState<string[]>([])
  const [filters, setFilters] = useState({
    limit: 50,
    minMomentum: 0,
    minReach: 0,
    minEngagement: 0
  })
  
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<number | null>(null)
  
  const fetchTrends = useCallback(async (limit: number) => {
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller
    
    try {
      setLoading(true)
      const response = await trendsService.listTrends({ limit })
      if (!controller.signal.aborted) {
        setTrends(response.trends || [])
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Failed to fetch trends:', error)
        setTrends([])
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    }
  }, [])
  
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchTrends(filters.limit), 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [filters.limit, fetchTrends])
  
  const filteredAndSortedTrends = useMemo(() => {
    const segment = filterSegments.find(s => s.id === selectedSegment)
    
    return trends
      .filter(segment?.filter || (() => true))
      .filter(trend => trend.word_representation?.some(word => word?.trim()))
      .filter(trend => {
        const metrics = computeMetrics(trend)
        return metrics.momentum >= filters.minMomentum &&
               trend.reach >= filters.minReach &&
               trend.median_engagement_rate >= filters.minEngagement &&
               (searchTokens.length === 0 || searchTokens.some(token => 
                 trend.word_representation.some(word => 
                   word.toLowerCase().includes(token.toLowerCase())
                 )
               ))
      })
      .sort((a, b) => {
        const metricsA = computeMetrics(a)
        const metricsB = computeMetrics(b)
        const sortMap = {
          momentum: metricsB.momentum - metricsA.momentum,
          heat: metricsB.heat - metricsA.heat,
          endurance: metricsB.endurance - metricsA.endurance,
          engagement: b.median_engagement_rate - a.median_engagement_rate,
          reach: b.reach - a.reach
        }
        return sortMap[sortBy] || sortMap.momentum
      })
  }, [trends, selectedSegment, sortBy, filters, searchTokens])
  
  const handleTrendSelect = useCallback((trendId: string) => {
    setSelectedTrends(prev => {
      const newSet = new Set(prev)
      newSet.has(trendId) ? newSet.delete(trendId) : newSet.add(trendId)
      return newSet
    })
  }, [])
  
  const lastUpdated = useMemo(() => 
    trends.length > 0 ? Math.max(...trends.map(t => t.created_at)) : 0, 
    [trends]
  )
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/10 to-transparent dark:from-blue-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                Social Trends
              </CardTitle>
              <p className="text-muted-foreground">
            Last updated {lastUpdated > 0 ? formatTime(lastUpdated) : 'never'}
          </p>
        </div>

      </div>
        </CardHeader>
      </Card>
      
      {/* Filters Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-50/50 dark:bg-slate-950/20 flex-shrink-0">
              <Filter className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Filters & Controls</CardTitle>
              <CardDescription>
                Customize your trend discovery experience
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
            {/* Segment filters */}
          <div className="space-y-3">
            <div className="text-sm font-medium">Trend Categories</div>
            <div className="flex flex-wrap gap-2">
              {filterSegments.map(segment => (
                <Button
                  key={segment.id}
                  variant={selectedSegment === segment.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedSegment(segment.id)}
                >
                  {segment.label}
                </Button>
              ))}
            </div>
            </div>
            
                         {/* Numeric filters */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <div className="text-sm font-medium">Limit</div>
              <div className="flex items-center gap-3">
                 <Input
                   type="range"
                   value={filters.limit}
                   onChange={(e) => setFilters(f => ({ ...f, limit: parseInt(e.target.value) }))}
                   max={500}
                   min={20}
                   step={10}
                  className="flex-1"
                 />
                <Badge variant="outline" className="min-w-[3rem] text-center">
                  {filters.limit}
                </Badge>
               </div>
              <p className="text-xs text-muted-foreground">Number of trends to load</p>
            </div>
            
            <div className="space-y-3">
              <div className="text-sm font-medium">Min Momentum</div>
              <div className="flex items-center gap-3">
                 <Input
                   type="range"
                   value={filters.minMomentum}
                   onChange={(e) => setFilters(f => ({ ...f, minMomentum: parseFloat(e.target.value) }))}
                   max={20}
                   min={0}
                   step={0.5}
                  className="flex-1"
                 />
                <Badge variant="outline" className="min-w-[3rem] text-center">
                  {filters.minMomentum}
                </Badge>
               </div>
              <p className="text-xs text-muted-foreground">Minimum trend momentum</p>
            </div>
            
            <div className="space-y-3">
              <div className="text-sm font-medium">Min Reach</div>
              <div className="flex items-center gap-3">
                 <Input
                   type="range"
                   value={filters.minReach}
                   onChange={(e) => setFilters(f => ({ ...f, minReach: parseInt(e.target.value) }))}
                   max={1000000}
                   min={0}
                   step={10000}
                  className="flex-1"
                 />
                <Badge variant="outline" className="min-w-[4rem] text-center">
                  {formatNumber(filters.minReach)}
                </Badge>
               </div>
              <p className="text-xs text-muted-foreground">Minimum audience reach</p>
            </div>
            
            <div className="space-y-3">
              <div className="text-sm font-medium">Min Engagement</div>
              <div className="flex items-center gap-3">
                 <Input
                   type="range"
                   value={filters.minEngagement * 100}
                   onChange={(e) => setFilters(f => ({ ...f, minEngagement: parseFloat(e.target.value) / 100 }))}
                   max={20}
                   min={0}
                   step={0.1}
                  className="flex-1"
                />
                <Badge variant="outline" className="min-w-[3rem] text-center">
                  {formatPercentage(filters.minEngagement)}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">Minimum engagement rate</p>
               </div>
             </div>
            
            {/* Sort and search */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <div className="text-sm font-medium">Sort by</div>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value as any)}
                className="border rounded-md px-3 py-2 bg-background text-foreground w-full sm:w-auto"
                >
                  <option value="momentum">Momentum</option>
                  <option value="heat">Heat</option>
                  <option value="endurance">Endurance</option>
                  <option value="engagement">Engagement</option>
                  <option value="reach">Reach</option>
                </select>
              </div>
              
            <div className="space-y-2 flex-1 sm:max-w-md">
              <div className="text-sm font-medium">Search keywords</div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Press Enter to add keywords..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const value = (e.target as HTMLInputElement).value.trim()
                      if (value && !searchTokens.includes(value)) {
                        setSearchTokens([...searchTokens, value])
                      }
                      ;(e.target as HTMLInputElement).value = ''
                    }
                  }}
                />
              </div>
              </div>
            </div>
            
            {/* Search tokens */}
            {searchTokens.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Active filters</div>
              <div className="flex flex-wrap gap-2">
                {searchTokens.map((token, i) => (
                  <Badge 
                    key={i} 
                    variant="secondary" 
                    className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors" 
                    onClick={() => {
                    setSearchTokens(searchTokens.filter((_, idx) => idx !== i))
                    }}
                  >
                    {token} ×
                  </Badge>
                ))}
              </div>
              </div>
            )}
        </CardContent>
      </Card>
      
      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Trends</CardTitle>
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{filteredAndSortedTrends.length}</div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <p className="text-xs text-blue-600">Active trends</p>
          </div>
        </CardContent>
      </Card>
      
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-green-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Median Momentum</CardTitle>
            <Activity className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {filteredAndSortedTrends.length > 0 
                ? filteredAndSortedTrends.map(t => computeMetrics(t).momentum).sort()[Math.floor(filteredAndSortedTrends.length / 2)].toFixed(1)
                : '0.0'}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <p className="text-xs text-green-600">Trend strength</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Engagement</CardTitle>
            <Heart className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-500">
              {formatPercentage(filteredAndSortedTrends.length > 0 
                ? filteredAndSortedTrends.reduce((sum, t) => sum + t.median_engagement_rate, 0) / filteredAndSortedTrends.length
                : 0)}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <p className="text-xs text-purple-600">User interaction</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-orange-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
            <Eye className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">
              {formatNumber(filteredAndSortedTrends.reduce((sum, t) => sum + t.reach, 0))}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <p className="text-xs text-orange-600">Impressions sum</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-emerald-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Today</CardTitle>
            <Zap className="h-5 w-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">
              {filteredAndSortedTrends.filter(t => (Date.now() / 1000) - t.created_at < 86400).length}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <p className="text-xs text-emerald-600">Fresh trends</p>
            </div>
          </CardContent>
        </Card>
      </div>
      

      
      {/* Main Content */}
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="grid">
            <BarChart3 className="w-4 h-4 mr-2" />
            Grid View
          </TabsTrigger>
          <TabsTrigger value="map">
            <Map className="w-4 h-4 mr-2" />
            Trend Map
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="grid" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="group hover:shadow-md transition-all duration-200 min-h-[500px]">
                  <CardHeader className="pb-6 px-8 pt-8">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 space-y-3">
                        <Skeleton className="h-6 w-3/4" />
                        <div className="flex flex-wrap gap-1.5">
                          {Array.from({ length: 4 }).map((_, j) => (
                            <Skeleton key={j} className="h-5 w-16 rounded-full" />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-start gap-2 flex-col">
                        <Skeleton className="h-6 w-20 rounded-full" />
                        <Skeleton className="h-6 w-12 rounded-full" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardHeader>
                  <CardContent className="space-y-4 px-8">
                    <div className="flex justify-center">
                      <Skeleton className="h-16 w-16 rounded-full" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {Array.from({ length: 4 }).map((_, j) => (
                        <Skeleton key={j} className="h-6 w-full" />
                      ))}
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-2 w-full rounded-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-2 w-full rounded-full" />
                    </div>
                  </CardContent>
                  <div className="border-t bg-background px-8 py-4 flex items-center justify-between">
                    <Skeleton className="h-4 w-32" />
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-6 w-20" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : filteredAndSortedTrends.length === 0 ? (
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50 flex-shrink-0">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold">No Trends Found</CardTitle>
                    <CardDescription>
                      Try adjusting your filters or search terms to discover active conversations.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
              {filteredAndSortedTrends.map(trend => (
                <TrendCard
                  key={trend.trend_id}
                  trend={trend}
                  isSelected={selectedTrends.has(trend.trend_id)}
                  onSelect={() => handleTrendSelect(trend.trend_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="map" className="mt-6">
          {loading ? (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-6 w-6 rounded" />
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-80" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Skeleton className="h-24 w-full rounded-lg" />
                  <Skeleton className="h-24 w-full rounded-lg" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full rounded-lg" />
                  ))}
                </div>
                <Skeleton className="h-96 w-full rounded-lg" />
                <div className="flex flex-wrap gap-6">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-4 w-32" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Trend Landscape Analysis</CardTitle>
                <CardDescription>
                  Interactive scatter plot showing trend behavior patterns. Click bubbles to select, hover for details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TrendMap 
                  trends={filteredAndSortedTrends} 
                  onTrendClick={(trend) => handleTrendSelect(trend.trend_id)}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>
    </div>
  )
}

export { SocialTrendsPage }
